// Two-Track §M2 · Command Center card: kick a signed-in Genesis
// discovery on any URL for the CALLER's org. Polls /runs/{id}/summary
// every 8 seconds and renders results with the shared DiscoverySummary.
import React, { useCallback, useEffect, useState } from "react";

import { DiscoverySummary } from "./DiscoverySummary";
import { api, formatApiError } from "../lib/api";

function ShareButton({ runId }) {
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [err, setErr] = useState(null);
  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post(`/genesis/runs/${runId}/share`, {});
      const path = r.data.share_url_path || `/discover/shared/${r.data.token}`;
      const full = `${window.location.origin}${path}`;
      setShareUrl(full);
      try {
        await navigator.clipboard.writeText(full);
      } catch (_) {}
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {!shareUrl ? (
        <button
          type="button"
          onClick={create}
          disabled={busy}
          data-testid="cc-discover-share"
          style={{
            padding: "6px 12px",
            background: "#c8a15a",
            color: "#1e1b16",
            border: "1px solid #c8a15a",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.55 : 1,
          }}
        >
          {busy ? "Creating…" : "Share read-only"}
        </button>
      ) : (
        <span
          data-testid="cc-discover-share-url"
          style={{ fontFamily: "var(--bo-mono, monospace)", fontSize: 11, color: "var(--bo-muted)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={shareUrl}
        >
          Copied · {shareUrl.replace(window.location.origin, "")}
        </span>
      )}
      {err ? <span style={{ fontSize: 11, color: "#b34a4a" }}>{err}</span> : null}
    </div>
  );
}

export function DiscoverFromUrlCard() {
  const [url, setUrl] = useState("");
  const [runId, setRunId] = useState(null);
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pollOnce = useCallback(async (id) => {
    try {
      const [s, sum] = await Promise.all([
        api.get(`/genesis/runs/${id}/status`),
        api.get(`/genesis/runs/${id}/summary`),
      ]);
      setStatus(s.data);
      setSummary(sum.data);
      return s.data.status;
    } catch (e) {
      setErr(formatApiError(e));
      return "error";
    }
  }, []);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    let attempts = 0;
    async function loop() {
      const st = await pollOnce(runId);
      attempts += 1;
      if (cancelled) return;
      if (st === "succeeded" || st === "complete" || st === "failed" || attempts > 40) {
        return;
      }
      setTimeout(loop, 8000);
    }
    loop();
    return () => {
      cancelled = true;
    };
  }, [runId, pollOnce]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url) return;
    setBusy(true);
    setErr(null);
    setSummary(null);
    setStatus(null);
    try {
      const r = await api.post("/genesis/discover", { root_url: url });
      setRunId(r.data.run_id);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setUrl("");
    setRunId(null);
    setStatus(null);
    setSummary(null);
    setErr(null);
  };

  return (
    <div className="bo-card" data-testid="cc-discover-card" style={{ marginBottom: 16 }}>
      <div className="bo-eyebrow">Genesis · M2 · Discover from URL</div>
      <h2 className="bo-card-title">Point Genesis at any website</h2>
      <p className="bo-card-lede">
        Crawls the URL, classifies each page, and extracts provisional identity, positioning, voice,
        product, and audience proposals into this org. Nothing is treated as truth until you review
        it in the Intelligence review queue.
      </p>
      {!runId ? (
        <form onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }} data-testid="cc-discover-form">
          <input
            type="url"
            required
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="cc-discover-url"
            style={{
              flex: "1 1 260px",
              padding: "10px 12px",
              border: "1px solid rgba(0,0,0,0.14)",
              borderRadius: 8,
              fontSize: 14,
              background: "#faf7f0",
            }}
          />
          <button
            type="submit"
            disabled={busy || !url}
            data-testid="cc-discover-submit"
            style={{
              padding: "10px 18px",
              background: "#1a1f2e",
              color: "#f0e6d0",
              border: "1px solid #1a1f2e",
              borderRadius: 8,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.55 : 1,
              fontWeight: 600,
            }}
          >
            {busy ? "Starting…" : "Run discovery →"}
          </button>
        </form>
      ) : (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "var(--bo-mono, monospace)", fontSize: 12, color: "var(--bo-muted)" }} data-testid="cc-discover-runid">
              Run <span>{runId.slice(0, 8)}…</span>
              {status ? <span style={{ marginLeft: 8 }}>· {status.status}</span> : null}
            </div>
            <ShareButton runId={runId} />
            <button
              type="button"
              onClick={reset}
              data-testid="cc-discover-reset"
              style={{
                marginLeft: "auto",
                padding: "6px 12px",
                background: "transparent",
                border: "1px solid rgba(0,0,0,0.14)",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Start a new one
            </button>
          </div>
          <DiscoverySummary summary={summary} testIdPrefix="cc-discover-summary" />
        </div>
      )}
      {err ? (
        <p style={{ color: "var(--bo-danger, #b34a4a)", fontSize: 12, marginTop: 8 }} data-testid="cc-discover-err">
          {err}
        </p>
      ) : null}
    </div>
  );
}
